/**
 * Client-side error reporting.
 *
 * Fire-and-forget POST to the API's /api/client-errors endpoint so that
 * frontend errors become durable/observable. Never throws and swallows
 * its own failures (including network errors) so reporting can never
 * break the app or cause recursive error loops.
 *
 * @format
 */

import Axios from "axios";

export type ErrorReportLevel = "error" | "warn" | "info";

export interface ErrorReport {
  message: string;
  level?: ErrorReportLevel;
  stack?: string;
  url?: string;
  context?: Record<string, unknown>;
}

/**
 * Report a client error to the API. Fire-and-forget: the returned promise
 * always resolves and never rejects.
 */
export function reportError(report: ErrorReport): void {
  try {
    const payload: ErrorReport = {
      message: String(report.message ?? ""),
      level: report.level ?? "error",
      stack: report.stack,
      url: report.url ?? (typeof window !== "undefined" ? window.location.href : undefined),
      context: report.context,
    };

    // Uses the app's axios instance so it inherits the configured baseURL
    // (direct API origin in prod, Vite proxy in dev).
    void Axios.post("/api/client-errors", payload).catch(() => {
      // Swallow: reporting failures must never surface.
    });
  } catch {
    // Swallow any synchronous failure (e.g. serialization issues).
  }
}

export default reportError;
